/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type {
  CellKeyDownEvent,
  RowClassParams,
  RowClickedEvent,
} from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import type {
  GridSectionData,
  SectionState,
} from '@sportninja/common/features/profile/stats/hooks/usePlayerStatsTab'
import type {
  SeasonGridRow,
  StatsRole,
  StatsSelectionAction,
} from '@sportninja/common/features/profile/stats/types'
import { getRoleTitle } from '@sportninja/common/features/profile/stats/utils/roleTitles'
import { getToggleSeasonRowId } from '@sportninja/common/features/profile/stats/utils/seasonToggle'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import { weightSemiBold } from 'src/components/css'
import DataGrid from 'src/components/ts-components/DataGrid'
import Text from 'src/components/ts-components/Text'

import { SEASON_GRID_HEADER_HEIGHT, SEASON_GRID_ROW_HEIGHT } from '../constants'
import {
  SEASON_GRID_CENTER_CLASS,
  SEASON_GRID_LABEL_CELL_CLASS,
  useSeasonGridColumns,
} from '../hooks/useSeasonGridColumns'
import { isLinkEvent } from '../utils/isLinkEvent'
import SeasonStatsGridSkeleton from './skeletons/SeasonStatsGridSkeleton'

interface SeasonStatsGridProps {
  grid: SectionState<GridSectionData>
  role: StatsRole
  sportName: string | null
  dispatch: (action: StatsSelectionAction) => void
}

const SEASON_ROW_CLASS = 'season-grid-season-row'
const NO_COLUMNS: GridSectionData['columns'] = []

const getRowId = ({ data }: { data: SeasonGridRow }) => data.id

const getRowClass = ({ data }: RowClassParams<SeasonGridRow>) =>
  data?.kind === 'season' ? SEASON_ROW_CLASS : undefined

/** Per-season stats for the selected role, with a Career row and expandable seasons. */
const SeasonStatsGrid = ({
  grid,
  role,
  sportName,
  dispatch,
}: SeasonStatsGridProps) => {
  'use memo'

  const { data, isLoading, error } = grid
  const columnDefs = useSeasonGridColumns(data?.columns ?? NO_COLUMNS)

  if (!data && !isLoading && !error) {
    return null
  }
  const hasRows = data !== undefined && data.rows.length > 0
  if (data && !hasRows && !error) {
    return null
  }

  const toggle = (row: SeasonGridRow | undefined) => {
    const rowId = getToggleSeasonRowId(row)
    if (rowId !== null) {
      dispatch({ type: 'toggleSeason', rowId })
    }
  }

  const handleRowClicked = (event: RowClickedEvent<SeasonGridRow>) => {
    if (!isLinkEvent(event.event)) {
      toggle(event.data)
    }
  }

  const handleCellKeyDown = (event: CellKeyDownEvent<SeasonGridRow>) => {
    if (
      event.event instanceof KeyboardEvent &&
      event.event.key === 'Enter' &&
      !isLinkEvent(event.event)
    ) {
      toggle(event.data)
    }
  }

  const renderBody = () => {
    if (isLoading && !data) {
      return <SeasonStatsGridSkeleton />
    }
    if (!data || !hasRows) {
      return (
        <Text variant='paragraphSmall' color={colors.ERROR_200}>
          Could not load season stats.
        </Text>
      )
    }
    const careerRow: SeasonGridRow = {
      kind: 'career',
      id: 'career',
      values: data.careerRow,
    }
    return (
      <div css={gridStyles}>
        <DataGrid<SeasonGridRow>
          rowData={data.rows}
          columnDefs={columnDefs}
          defaultColDef={{ sortable: false }}
          pinnedTopRowData={[careerRow]}
          domLayout='autoHeight'
          autoHeightMinBodyHeight={0}
          rowHeight={SEASON_GRID_ROW_HEIGHT}
          headerHeight={SEASON_GRID_HEADER_HEIGHT}
          getRowId={getRowId}
          getRowClass={getRowClass}
          onRowClicked={handleRowClicked}
          onCellKeyDown={handleCellKeyDown}
          horizontalScrollAffordance
        />
      </div>
    )
  }

  return (
    <section css={sectionStyles}>
      <div css={headerStyles}>
        <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(`${getRoleTitle(role, sportName)} Statistics`)}
        </Text>
        <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
          Click a season to see its games
        </Text>
      </div>
      {renderBody()}
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const headerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

/**
 * No row / header / hover / border repainting: the house look belongs to the
 * shared `dataGridTheme` (as in `operationsGridStyle`), so this table matches
 * every other grid. Only what is specific to this table stays below.
 */
const gridStyles = css`
  width: 100%;
  min-width: 0;

  /* rowHeight sizes the row box only; cell line-height follows the theme's
     --ag-row-height, so it is matched here or text sits ~3px high. Declared on
     .ag-root-wrapper because the theme sets it on an element below the outer
     wrapper. The Career row's PRIMARY text is the one colour kept. */
  .ag-root-wrapper {
    --ag-row-height: ${SEASON_GRID_ROW_HEIGHT}px;
    --ag-pinned-row-text-color: ${colors.PRIMARY};
  }

  .ag-row-pinned .ag-cell {
    ${weightSemiBold}
  }

  .${SEASON_GRID_LABEL_CELL_CLASS} {
    display: flex;
    align-items: center;
  }

  .${SEASON_ROW_CLASS} {
    cursor: pointer;
  }

  .${SEASON_GRID_CENTER_CLASS} .ag-header-cell-label {
    justify-content: center;
  }
`

export default SeasonStatsGrid
